'use client';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ThreadPreview {
  id: string; // consultation_threads.id
  conversationId: string;
  bookingTitle: string;
  bookingDate: string | null;
  bookingStatus: string;
  threadType: 'pre_booking' | 'post_booking' | 'follow_up' | 'general';
  otherUserId: string;
  otherUserName: string;
  myRole: 'client' | 'consultant';
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  updatedAt: string;
}

interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  isDeleted: boolean;
  createdAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function getInitials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

const AVATAR_COLORS = [
  'bg-teal-500', 'bg-emerald-500', 'bg-sky-500', 'bg-violet-500',
  'bg-rose-500', 'bg-amber-500', 'bg-indigo-500', 'bg-pink-500',
];
function avatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const THREAD_TYPE_LABELS: Record<string, string> = {
  pre_booking: 'Pre-Session',
  post_booking: 'Post-Session',
  follow_up: 'Follow-up',
  general: 'General',
};

const BOOKING_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-sky-100 text-sky-700',
  cancelled: 'bg-red-100 text-red-700',
};

// ─── New Thread Modal ─────────────────────────────────────────────────────────
interface NewThreadModalProps {
  onClose: () => void;
  onCreated: () => void;
  currentUserId: string;
}

function NewThreadModal({ onClose, onCreated, currentUserId }: NewThreadModalProps) {
  const supabase = createClient();
  const [step, setStep] = useState<'search' | 'compose'>('search');
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<{ id: string; full_name: string; email: string }[]>([]);
  const [selectedUser, setSelectedUser] = useState<{ id: string; full_name: string } | null>(null);
  const [bookingTitle, setBookingTitle] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [threadType, setThreadType] = useState<'pre_booking' | 'post_booking' | 'follow_up' | 'general'>('general');
  const [firstMessage, setFirstMessage] = useState('');
  const [myRole, setMyRole] = useState<'client' | 'consultant'>('client');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (query.trim().length < 2) { setUsers([]); return; }
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .neq('id', currentUserId)
        .ilike('full_name', `%${query}%`)
        .limit(8);
      setUsers(data ?? []);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, currentUserId, supabase]);

  const handleCreate = async () => {
    if (!selectedUser || !bookingTitle.trim() || !firstMessage.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      // Create conversation
      const { data: conv, error: convErr } = await supabase
        .from('conversations')
        .insert({})
        .select('id')
        .single();
      if (convErr || !conv) throw new Error('Failed to create conversation');

      // Add participants
      await supabase.from('conversation_participants').insert([
        { conversation_id: conv.id, user_id: currentUserId },
        { conversation_id: conv.id, user_id: selectedUser.id },
      ]);

      // Create thread
      const clientId = myRole === 'client' ? currentUserId : selectedUser.id;
      const consultantId = myRole === 'consultant' ? currentUserId : selectedUser.id;
      await supabase.from('consultation_threads').insert({
        conversation_id: conv.id,
        client_user_id: clientId,
        consultant_user_id: consultantId,
        booking_title: bookingTitle.trim(),
        booking_date: bookingDate || null,
        booking_status: 'pending',
        thread_type: threadType,
      });

      // Send first message
      await supabase.from('private_messages').insert({
        conversation_id: conv.id,
        sender_id: currentUserId,
        content: firstMessage.trim(),
      });

      onCreated();
    } catch (e: any) {
      setError(e.message ?? 'Something went wrong');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-lg border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-700 text-foreground">New Consultation Thread</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {step === 'search' ? (
            <>
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">Search consultant or client</label>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Type a name…"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              {users.length > 0 && (
                <ul className="space-y-1 max-h-52 overflow-y-auto">
                  {users.map((u) => (
                    <li key={u.id}>
                      <button
                        onClick={() => { setSelectedUser(u); setStep('compose'); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted text-left transition-colors"
                      >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-600 flex-shrink-0 ${avatarColor(u.id)}`}>
                          {getInitials(u.full_name)}
                        </div>
                        <div>
                          <p className="text-sm font-600 text-foreground">{u.full_name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {query.trim().length >= 2 && users.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No users found</p>
              )}
            </>
          ) : (
            <>
              {/* Selected user */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-600 flex-shrink-0 ${avatarColor(selectedUser!.id)}`}>
                  {getInitials(selectedUser!.full_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-600 text-foreground">{selectedUser!.full_name}</p>
                </div>
                <button onClick={() => { setSelectedUser(null); setStep('search'); }} className="text-muted-foreground hover:text-foreground">
                  <Icon name="XMarkIcon" size={16} />
                </button>
              </div>

              {/* My role */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">My role in this thread</label>
                <div className="flex gap-2">
                  {(['client', 'consultant'] as const).map((r) => (
                    <button
                      key={r}
                      onClick={() => setMyRole(r)}
                      className={`flex-1 py-2 rounded-xl text-sm font-600 border transition-colors ${myRole === r ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
                    >
                      {r === 'client' ? 'Client' : 'Consultant'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Thread type */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">Thread type</label>
                <select
                  value={threadType}
                  onChange={(e) => setThreadType(e.target.value as typeof threadType)}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="pre_booking">Pre-Session</option>
                  <option value="post_booking">Post-Session</option>
                  <option value="follow_up">Follow-up</option>
                  <option value="general">General</option>
                </select>
              </div>

              {/* Booking title */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">Session / topic title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={bookingTitle}
                  onChange={(e) => setBookingTitle(e.target.value)}
                  placeholder="e.g. Sukuk Structuring Consultation"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {/* Booking date (optional) */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">Session date (optional)</label>
                <input
                  type="date"
                  value={bookingDate}
                  onChange={(e) => setBookingDate(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {/* First message */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">First message <span className="text-red-500">*</span></label>
                <textarea
                  value={firstMessage}
                  onChange={(e) => setFirstMessage(e.target.value)}
                  rows={3}
                  placeholder="Introduce yourself or describe what you'd like to discuss…"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <button
                onClick={handleCreate}
                disabled={creating}
                className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Start Thread'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ConsultationMessagesPage() {
  const { user } = useAuth();
  const supabase = createClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [threads, setThreads] = useState<ThreadPreview[]>([]);
  const [activeThread, setActiveThread] = useState<ThreadPreview | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showNewThread, setShowNewThread] = useState(false);
  const [filterType, setFilterType] = useState<'all' | 'pre_booking' | 'post_booking' | 'follow_up' | 'general'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // ─── Load threads ──────────────────────────────────────────────────────────
  const loadThreads = useCallback(async () => {
    if (!user) return;
    setLoadingThreads(true);
    try {
      const { data: threadRows } = await supabase
        .from('consultation_threads')
        .select('*')
        .or(`client_user_id.eq.${user.id},consultant_user_id.eq.${user.id}`)
        .order('updated_at', { ascending: false });

      if (!threadRows || threadRows.length === 0) {
        setThreads([]);
        return;
      }

      const previews: ThreadPreview[] = [];
      for (const t of threadRows) {
        const otherUserId = t.client_user_id === user.id ? t.consultant_user_id : t.client_user_id;
        const myRole: 'client' | 'consultant' = t.client_user_id === user.id ? 'client' : 'consultant';

        // Fetch other user profile
        const { data: otherProfile } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .eq('id', otherUserId)
          .single();

        // Fetch last message
        const { data: lastMsgRows } = await supabase
          .from('private_messages')
          .select('content, created_at')
          .eq('conversation_id', t.conversation_id)
          .eq('is_deleted', false)
          .order('created_at', { ascending: false })
          .limit(1);

        // Fetch unread count
        const { data: myParticipant } = await supabase
          .from('conversation_participants')
          .select('last_read_at')
          .eq('conversation_id', t.conversation_id)
          .eq('user_id', user.id)
          .single();

        let unreadCount = 0;
        if (myParticipant?.last_read_at) {
          const { count } = await supabase
            .from('private_messages')
            .select('id', { count: 'exact', head: true })
            .eq('conversation_id', t.conversation_id)
            .neq('sender_id', user.id)
            .gt('created_at', myParticipant.last_read_at);
          unreadCount = count ?? 0;
        } else {
          const { count } = await supabase
            .from('private_messages')
            .select('id', { count: 'exact', head: true })
            .eq('conversation_id', t.conversation_id)
            .neq('sender_id', user.id);
          unreadCount = count ?? 0;
        }

        previews.push({
          id: t.id,
          conversationId: t.conversation_id,
          bookingTitle: t.booking_title,
          bookingDate: t.booking_date,
          bookingStatus: t.booking_status,
          threadType: t.thread_type,
          otherUserId,
          otherUserName: otherProfile?.full_name ?? 'Unknown User',
          myRole,
          lastMessage: lastMsgRows?.[0]?.content ?? '',
          lastMessageTime: lastMsgRows?.[0]?.created_at ?? t.updated_at,
          unreadCount,
          updatedAt: t.updated_at,
        });
      }
      setThreads(previews);
    } finally {
      setLoadingThreads(false);
    }
  }, [user, supabase]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // ─── Load messages for active thread ──────────────────────────────────────
  const loadMessages = useCallback(async (conversationId: string) => {
    setLoadingMessages(true);
    try {
      const { data } = await supabase
        .from('private_messages')
        .select('id, conversation_id, sender_id, content, is_deleted, created_at')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
      setMessages((data ?? []).map((m: any) => ({
        id: m.id,
        conversationId: m.conversation_id,
        senderId: m.sender_id,
        content: m.content,
        isDeleted: m.is_deleted,
        createdAt: m.created_at,
      })));
    } finally {
      setLoadingMessages(false);
    }
  }, [supabase]);

  useEffect(() => {
    if (!activeThread) return;
    loadMessages(activeThread.conversationId);

    // Mark as read
    if (user) {
      supabase
        .from('conversation_participants')
        .update({ last_read_at: new Date().toISOString() })
        .eq('conversation_id', activeThread.conversationId)
        .eq('user_id', user.id)
        .then(() => {});
    }

    // Real-time subscription
    const channel = supabase
      .channel(`consultation-thread-${activeThread.conversationId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'private_messages',
        filter: `conversation_id=eq.${activeThread.conversationId}`,
      }, (payload) => {
        const m = payload.new as any;
        setMessages((prev) => [...prev, {
          id: m.id,
          conversationId: m.conversation_id,
          senderId: m.sender_id,
          content: m.content,
          isDeleted: m.is_deleted,
          createdAt: m.created_at,
        }]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [activeThread, user, supabase, loadMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── Send message ──────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!user || !activeThread || !newMessage.trim() || sending) return;
    setSending(true);
    const content = newMessage.trim();
    setNewMessage('');
    try {
      await supabase.from('private_messages').insert({
        conversation_id: activeThread.conversationId,
        sender_id: user.id,
        content,
      });
      // Update thread updated_at
      await supabase
        .from('consultation_threads')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', activeThread.id);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ─── Filtered threads ──────────────────────────────────────────────────────
  const filteredThreads = threads.filter((t) => {
    const matchesType = filterType === 'all' || t.threadType === filterType;
    const matchesSearch = !searchQuery.trim() ||
      t.bookingTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.otherUserName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const totalUnread = threads.reduce((sum, t) => sum + t.unreadCount, 0);

  return (
    <AppLayout activeRoute="/consultation-messages">
      <div className="flex h-[calc(100vh-64px)] overflow-hidden">
        {/* ── Thread List Panel ─────────────────────────────────────────── */}
        <div className={`flex flex-col border-r border-border bg-card ${activeThread ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-96 flex-shrink-0`}>
          {/* Header */}
          <div className="px-4 py-4 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-700 text-foreground">Consultation Messages</h1>
                {totalUnread > 0 && (
                  <span className="bg-primary text-primary-foreground text-xs font-700 px-2 py-0.5 rounded-full">
                    {totalUnread}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowNewThread(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:opacity-90 transition-opacity"
              >
                <Icon name="PlusIcon" size={16} />
                <span>New</span>
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search threads…"
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            {/* Filter tabs */}
            <div className="flex gap-1 overflow-x-auto scrollbar-none">
              {(['all', 'pre_booking', 'post_booking', 'follow_up', 'general'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-600 whitespace-nowrap transition-colors ${filterType === type ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                >
                  {type === 'all' ? 'All' : THREAD_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </div>

          {/* Thread list */}
          <div className="flex-1 overflow-y-auto">
            {loadingThreads ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
                  <Icon name="ChatBubbleLeftRightIcon" size={28} className="text-muted-foreground" />
                </div>
                <p className="text-sm font-600 text-foreground mb-1">No threads yet</p>
                <p className="text-xs text-muted-foreground mb-4">Start a conversation with a consultant or client</p>
                <button
                  onClick={() => setShowNewThread(true)}
                  className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:opacity-90"
                >
                  New Thread
                </button>
              </div>
            ) : (
              filteredThreads.map((thread) => (
                <button
                  key={thread.id}
                  onClick={() => setActiveThread(thread)}
                  className={`w-full flex items-start gap-3 px-4 py-3.5 border-b border-border/50 hover:bg-muted/50 transition-colors text-left ${activeThread?.id === thread.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                >
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-600 flex-shrink-0 ${avatarColor(thread.otherUserId)}`}>
                    {getInitials(thread.otherUserName)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className="text-sm font-600 text-foreground truncate">{thread.otherUserName}</p>
                      <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{timeAgo(thread.lastMessageTime)}</span>
                    </div>
                    <p className="text-xs font-600 text-primary truncate mb-0.5">{thread.bookingTitle}</p>
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded-md font-500 ${BOOKING_STATUS_COLORS[thread.bookingStatus] ?? 'bg-muted text-muted-foreground'}`}>
                        {thread.bookingStatus}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground font-500">
                        {THREAD_TYPE_LABELS[thread.threadType]}
                      </span>
                      <span className="text-xs text-muted-foreground font-500 capitalize">
                        ({thread.myRole})
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground truncate">{thread.lastMessage || 'No messages yet'}</p>
                      {thread.unreadCount > 0 && (
                        <span className="ml-2 bg-primary text-primary-foreground text-xs font-700 px-1.5 py-0.5 rounded-full min-w-[20px] text-center flex-shrink-0">
                          {thread.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ── Chat Panel ────────────────────────────────────────────────── */}
        {activeThread ? (
          <div className="flex flex-col flex-1 min-w-0 bg-background">
            {/* Chat header */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border bg-card">
              <button
                onClick={() => setActiveThread(null)}
                className="md:hidden p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
              >
                <Icon name="ArrowLeftIcon" size={20} />
              </button>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-600 flex-shrink-0 ${avatarColor(activeThread.otherUserId)}`}>
                {getInitials(activeThread.otherUserName)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-700 text-foreground truncate">{activeThread.otherUserName}</p>
                <p className="text-xs text-muted-foreground truncate">{activeThread.bookingTitle}</p>
              </div>
              {/* Thread meta badges */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2 py-1 rounded-lg font-600 ${BOOKING_STATUS_COLORS[activeThread.bookingStatus] ?? 'bg-muted text-muted-foreground'}`}>
                  {activeThread.bookingStatus}
                </span>
                <span className="text-xs px-2 py-1 rounded-lg bg-muted text-muted-foreground font-500">
                  {THREAD_TYPE_LABELS[activeThread.threadType]}
                </span>
                {activeThread.bookingDate && (
                  <span className="text-xs px-2 py-1 rounded-lg bg-sky-100 text-sky-700 font-500 hidden sm:inline">
                    {new Date(activeThread.bookingDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-3">
                    <Icon name="ChatBubbleOvalLeftEllipsisIcon" size={28} className="text-muted-foreground" />
                  </div>
                  <p className="text-sm font-600 text-foreground mb-1">No messages yet</p>
                  <p className="text-xs text-muted-foreground">Send the first message to get started</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isMe = msg.senderId === user?.id;
                  return (
                    <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      {!isMe && (
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-600 flex-shrink-0 mr-2 mt-1 ${avatarColor(activeThread.otherUserId)}`}>
                          {getInitials(activeThread.otherUserName)}
                        </div>
                      )}
                      <div className={`max-w-[70%] ${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                        <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isMe ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-card border border-border text-foreground rounded-bl-sm'}`}>
                          {msg.isDeleted ? (
                            <span className="italic opacity-60">Message deleted</span>
                          ) : (
                            msg.content
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground px-1">{formatTime(msg.createdAt)}</span>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t border-border bg-card">
              <div className="flex items-end gap-2">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message… (Enter to send)"
                  rows={1}
                  className="flex-1 px-4 py-2.5 rounded-2xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none max-h-32"
                  style={{ minHeight: '44px' }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="p-2.5 rounded-2xl bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-40 flex-shrink-0"
                >
                  <Icon name="PaperAirplaneIcon" size={20} />
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5 px-1">
                Shift+Enter for new line · Enter to send
              </p>
            </div>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center bg-background">
            <div className="text-center">
              <div className="w-20 h-20 rounded-3xl bg-muted flex items-center justify-center mx-auto mb-4">
                <Icon name="ChatBubbleLeftRightIcon" size={40} className="text-muted-foreground" />
              </div>
              <h2 className="text-lg font-700 text-foreground mb-2">Select a thread</h2>
              <p className="text-sm text-muted-foreground max-w-xs">
                Choose a consultation thread from the left to view messages, or start a new one.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* New Thread Modal */}
      {showNewThread && user && (
        <NewThreadModal
          currentUserId={user.id}
          onClose={() => setShowNewThread(false)}
          onCreated={() => { setShowNewThread(false); loadThreads(); }}
        />
      )}
    </AppLayout>
  );
}
